import React, { useState } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { DatabaseZap, Loader2, CheckCircle2 } from 'lucide-react';

/**
 * One-off "Seed H/O Support starting data" button for Settings > Database.
 * Calls POST /order-book-reports/ho-support/seed, which runs in-process
 * against whichever database THIS running instance is connected to — so
 * clicking this on the deployed production site seeds production directly,
 * with no need for shell/terminal access to it. Safe to click more than
 * once: already-seeded rows are skipped (upsert on natural key), never
 * duplicated or overwritten.
 */
export default function HOSupportSeedButton() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const runSeed = async () => {
    if (!window.confirm(
      "This writes the H/O Support starting data (New Claims, Live Accounts, Supplier Matrix) " +
      "into THIS environment's database — whichever site you're currently on (preview or the " +
      "deployed production site), not a fixed target. Double-check you're on the right one.\n\n" +
      "Continue?"
    )) return;
    setRunning(true);
    setResult(null);
    try {
      const res = await api.post(`${API}/order-book-reports/ho-support/seed`);
      setResult({ ok: true, ...res.data });
      toast.success('H/O Support seed complete');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Seed failed';
      setResult({ ok: false, message: detail });
      toast.error(detail);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DatabaseZap className="w-5 h-5" style={{ color: '#ee7203' }} />
          H/O Support — Seed Starting Data
        </CardTitle>
        <CardDescription>
          One-off: populates New Claims, Live Accounts and Supplier Matrix from the original
          Encon_HO_Support.xlsx baseline into whichever database this environment is connected
          to. Safe to run more than once — already-seeded rows are skipped, never duplicated.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          onClick={runSeed}
          disabled={running}
          className="text-white"
          style={{ backgroundColor: '#ee7203' }}
          data-testid="ho-support-seed-btn"
        >
          {running ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Seeding…</>
          ) : (
            'Run H/O Support Seed'
          )}
        </Button>

        {result && (
          <div
            className={`mt-4 p-3 rounded-lg border text-sm ${result.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
            data-testid="ho-support-seed-result"
          >
            {result.ok ? (
              <div className="space-y-1 text-green-800">
                <p className="flex items-center gap-1.5 font-semibold"><CheckCircle2 className="w-4 h-4" /> Seed complete</p>
                <p>New Claims: {result.new_claims_inserted} inserted, {result.new_claims_skipped} already existed</p>
                <p>Live Accounts: {result.live_accounts_inserted} inserted, {result.live_accounts_skipped} already existed</p>
                <p>Supplier Matrix: {result.matrix_inserted} inserted, {result.matrix_skipped} already existed</p>
                {result.errors?.length > 0 && (
                  <p className="text-amber-700">⚠️ {result.errors.length} row(s) skipped — see backend logs for details.</p>
                )}
              </div>
            ) : (
              <p className="text-red-700">{result.message}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
